import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import * as BABYLON from '@babylonjs/core'

export interface RoomArtwork {
  img: string
  ratio: number
  w: number
  /** -1 = left wall, 1 = right wall, 0 = back wall */
  side: -1 | 0 | 1
  z: number
  name: string
  loves: number
  cms: number
  voice?: boolean
  pin?: boolean
  hearts?: boolean
}

export interface RoomFocus {
  index: number
  name: string
  loves: number
  cms: number
  voice?: boolean
  pin?: boolean
}

export interface RoomWalkthroughHandle {
  toggleTour: () => void
}

export interface RoomWalkthroughProps {
  artworks: RoomArtwork[]
  /** Base path artworks are served from, e.g. '/marketing/landing-v2' */
  imageBase: string
  onFocusChange?: (focus: RoomFocus | null) => void
  onPlayingChange?: (playing: boolean) => void
  className?: string
  children?: React.ReactNode
}

const Root = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  background: '#120e1b',
})

const Canvas = styled('canvas')({
  width: '100%',
  height: '100%',
  display: 'block',
  outline: 'none',
  touchAction: 'none',
  cursor: 'grab',
  '&:active': { cursor: 'grabbing' },
})

const OverlayHost = styled('div')({
  position: 'absolute',
  inset: 0,
  zIndex: 6,
  pointerEvents: 'none',
})

const LoopFade = styled('div')({
  position: 'absolute',
  inset: 0,
  zIndex: 12,
  background: '#0e0d12',
  opacity: 0,
  pointerEvents: 'none',
  transition: 'opacity 480ms cubic-bezier(0.16, 1, 0.3, 1)',
})

/**
 * Live Babylon.js gallery walkthrough — a framed-photo corridor with warm
 * spotlights, a human-paced auto-guided walk that dwells on each print,
 * projected comment pins/notes, floating reactions, and drifting visitor
 * presence orbs. Ported from the prototype; the walker physics (damped
 * accel/brake, footstep bob, gaze lead-then-settle) are
 * unchanged from spec — deliberately subtle so it reads as a person
 * walking, not a seasick dolly shot.
 */
export const RoomWalkthrough3D = forwardRef<RoomWalkthroughHandle, RoomWalkthroughProps>(
  function RoomWalkthrough3D(
    { artworks, imageBase, onFocusChange, onPlayingChange, className, children },
    ref,
  ) {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const hostRef = useRef<HTMLDivElement>(null)
    const fadeRef = useRef<HTMLDivElement>(null)
    const playingRef = useRef(true)
    const toggleRef = useRef<() => void>(() => {})

    useImperativeHandle(ref, () => ({ toggleTour: () => toggleRef.current() }), [])

    useEffect(() => {
      const canvas = canvasRef.current
      const host = hostRef.current
      const fade = fadeRef.current
      if (!canvas || !host || !fade || artworks.length === 0) return

      const B = BABYLON
      const engine = new B.Engine(canvas, true, { antialias: true, adaptToDeviceRatio: true })
      const scene = new B.Scene(engine)
      scene.clearColor = B.Color4.FromHexString('#120e1bff')
      scene.fogMode = B.Scene.FOGMODE_EXP2
      scene.fogDensity = 0.012
      scene.fogColor = B.Color3.FromHexString('#161020')

      /* ---------- Room shell ---------- */
      const W = 11
      const H = 5.2
      const D = 30
      const wallMat = new B.StandardMaterial('wall', scene)
      wallMat.diffuseColor = B.Color3.FromHexString('#40365a')
      wallMat.specularColor = B.Color3.Black()
      wallMat.backFaceCulling = false
      wallMat.twoSidedLighting = true
      wallMat.maxSimultaneousLights = 8
      const wallMatDeep = wallMat.clone('wallDeep')
      wallMatDeep.diffuseColor = B.Color3.FromHexString('#362d4c')
      const floorMat = new B.StandardMaterial('floor', scene)
      floorMat.diffuseColor = B.Color3.FromHexString('#241c33')
      floorMat.specularColor = B.Color3.FromHexString('#4a3d63')
      floorMat.specularPower = 140
      floorMat.maxSimultaneousLights = 8
      const ceilMat = new B.StandardMaterial('ceil', scene)
      ceilMat.diffuseColor = B.Color3.FromHexString('#241c33')
      ceilMat.specularColor = B.Color3.Black()
      ceilMat.backFaceCulling = false
      ceilMat.twoSidedLighting = true
      ceilMat.maxSimultaneousLights = 8

      const floor = B.MeshBuilder.CreateGround('g', { width: W, height: D }, scene)
      floor.position.z = D / 2 - 12
      floor.material = floorMat
      const ceil = B.MeshBuilder.CreatePlane('c', { width: W, height: D }, scene)
      ceil.rotation.x = -Math.PI / 2
      ceil.position.set(0, H, D / 2 - 12)
      ceil.material = ceilMat
      const mkWall = (w: number, h: number, pos: BABYLON.Vector3, rotY: number, mat: BABYLON.StandardMaterial) => {
        const p = B.MeshBuilder.CreatePlane('w', { width: w, height: h }, scene)
        p.position.copyFrom(pos)
        p.rotation.y = rotY
        p.material = mat
        return p
      }
      mkWall(D, H, new B.Vector3(-W / 2, H / 2, D / 2 - 12), Math.PI / 2, wallMat)
      mkWall(D, H, new B.Vector3(W / 2, H / 2, D / 2 - 12), -Math.PI / 2, wallMat)
      mkWall(W, H, new B.Vector3(0, H / 2, D - 12), Math.PI, wallMatDeep)
      mkWall(W, H, new B.Vector3(0, H / 2, -12), 0, wallMatDeep)

      const stripMat = new B.StandardMaterial('strip', scene)
      stripMat.emissiveColor = B.Color3.FromHexString('#4a3a20')
      stripMat.disableLighting = true
      ;[-1, 1].forEach((s) => {
        const strip = B.MeshBuilder.CreateBox('st', { width: 0.03, height: 0.02, depth: D }, scene)
        strip.position.set(s * (W / 2 - 0.02), 0.04, D / 2 - 12)
        strip.material = stripMat
      })

      const lampGlowMat = new B.StandardMaterial('lg', scene)
      lampGlowMat.emissiveColor = B.Color3.FromHexString('#f0d9ac')
      lampGlowMat.disableLighting = true
      const lampRimMat = new B.StandardMaterial('lr', scene)
      lampRimMat.diffuseColor = B.Color3.FromHexString('#0d0b10')
      lampRimMat.specularColor = B.Color3.Black()
      for (let z = -9; z <= 16; z += 5) {
        const rim = B.MeshBuilder.CreateCylinder('rim', { height: 0.04, diameter: 0.34 }, scene)
        rim.position.set(0, H - 0.02, z)
        rim.material = lampRimMat
        const disc = B.MeshBuilder.CreateDisc('lamp', { radius: 0.12 }, scene)
        disc.rotation.x = -Math.PI / 2
        disc.position.set(0, H - 0.045, z)
        disc.material = lampGlowMat
      }

      /* ---------- Lighting ---------- */
      const amb = new B.HemisphericLight('amb', new B.Vector3(0, 1, 0), scene)
      amb.intensity = 0.72
      amb.diffuse = B.Color3.FromHexString('#c3b3de')
      amb.groundColor = B.Color3.FromHexString('#443355')
      const fill = new B.PointLight('fill', new B.Vector3(0, H - 0.6, 2), scene)
      fill.diffuse = B.Color3.FromHexString('#8d7ab0')
      fill.intensity = 0.5
      fill.range = 30
      const fill2 = new B.PointLight('fill2', new B.Vector3(0, H - 0.6, 12), scene)
      fill2.diffuse = B.Color3.FromHexString('#8d7ab0')
      fill2.intensity = 0.5
      fill2.range = 30

      /* ---------- Artworks ---------- */
      const texFor = (key: string) => {
        const t = new B.Texture(`${imageBase}/${key}.jpg`, scene)
        t.anisotropicFilteringLevel = 8
        return t
      }
      const frameMat = new B.StandardMaterial('fr', scene)
      frameMat.diffuseColor = B.Color3.FromHexString('#0d0b10')
      frameMat.specularColor = B.Color3.FromHexString('#6b5a3a')
      frameMat.specularPower = 90
      frameMat.maxSimultaneousLights = 8
      const matMat = new B.StandardMaterial('mat', scene)
      matMat.diffuseColor = B.Color3.FromHexString('#efe9dd')
      matMat.specularColor = B.Color3.Black()
      matMat.maxSimultaneousLights = 8

      type ArtRuntime = RoomArtwork & {
        center: BABYLON.Vector3
        normal: BABYLON.Vector3
        viewPos: BABYLON.Vector3
      }
      const arts: ArtRuntime[] = artworks.map((a) => ({ ...a }) as ArtRuntime)

      arts.forEach((a) => {
        const ph = a.w / a.ratio
        const y = 2.35
        let pos: BABYLON.Vector3
        let rotY: number
        if (a.side === -1) {
          pos = new B.Vector3(-W / 2 + 0.001, y, a.z)
          rotY = Math.PI / 2
        } else if (a.side === 1) {
          pos = new B.Vector3(W / 2 - 0.001, y, a.z)
          rotY = -Math.PI / 2
        } else {
          pos = new B.Vector3(0, y, D - 12 - 0.001)
          rotY = Math.PI
        }
        const n =
          a.side === -1 ? new B.Vector3(1, 0, 0) : a.side === 1 ? new B.Vector3(-1, 0, 0) : new B.Vector3(0, 0, -1)

        const frame = B.MeshBuilder.CreateBox('f', { width: a.w + 0.26, height: ph + 0.26, depth: 0.09 }, scene)
        frame.position = pos.add(n.scale(0.045))
        frame.rotation.y = rotY
        frame.material = frameMat
        const mat = B.MeshBuilder.CreateBox('m', { width: a.w + 0.14, height: ph + 0.14, depth: 0.02 }, scene)
        mat.position = pos.add(n.scale(0.095))
        mat.rotation.y = rotY
        mat.material = matMat

        const photoMat = new B.StandardMaterial(`p${a.img}`, scene)
        photoMat.diffuseTexture = texFor(a.img)
        photoMat.emissiveTexture = photoMat.diffuseTexture
        photoMat.emissiveColor = B.Color3.FromHexString('#5e5e5e')
        photoMat.specularColor = B.Color3.FromHexString('#161616')
        photoMat.specularPower = 220
        photoMat.maxSimultaneousLights = 8
        const photo = B.MeshBuilder.CreatePlane('ph', { width: a.w, height: ph }, scene)
        photo.position = pos.add(n.scale(0.115))
        photo.rotation.y = a.side === 0 ? 0 : rotY + Math.PI
        photo.material = photoMat

        const lp = pos.add(n.scale(1.7))
        lp.y = H - 0.25
        const spot = new B.SpotLight('sp', lp, pos.subtract(lp).normalize(), Math.PI / 3.2, 18, scene)
        spot.diffuse = B.Color3.FromHexString('#ffd9a0')
        spot.intensity = 9
        const fixture = B.MeshBuilder.CreateCylinder('fx', { height: 0.22, diameter: 0.09 }, scene)
        fixture.position = lp.clone()
        fixture.position.y = H - 0.11
        const toArt = pos.subtract(lp).normalize()
        fixture.rotation.z = Math.atan2(toArt.x, -toArt.y) * 0.5
        fixture.rotation.x = Math.atan2(toArt.z, -toArt.y) * -0.5
        fixture.material = frameMat
        const bulb = B.MeshBuilder.CreateSphere('bl', { diameter: 0.055 }, scene)
        bulb.position = lp.clone()
        bulb.position.y -= 0.02
        const bulbM = new B.StandardMaterial('blm', scene)
        bulbM.emissiveColor = B.Color3.FromHexString('#ffe9c4')
        bulbM.disableLighting = true
        bulb.material = bulbM

        a.center = pos.add(n.scale(0.13))
        a.normal = n
        a.viewPos = pos.add(n.scale(2.6 + a.w * 1.05))
        if (a.side !== 0) a.viewPos.z -= 1.4
        else a.viewPos.x -= 1.1
        a.viewPos.y = 1.58
      })

      /* ---------- Camera: a human being, not a dolly ---------- */
      const EYE = 1.62
      const cam = new B.UniversalCamera('cam', new B.Vector3(0, EYE, -10.5), scene)
      cam.minZ = 0.05
      cam.fov = 0.92

      const pipe = new B.DefaultRenderingPipeline('pipe', true, scene, [cam])
      pipe.fxaaEnabled = true
      pipe.bloomEnabled = true
      pipe.bloomThreshold = 0.72
      pipe.bloomWeight = 0.28
      pipe.bloomKernel = 48
      pipe.bloomScale = 0.5
      pipe.imageProcessingEnabled = true
      pipe.imageProcessing.toneMappingEnabled = true
      pipe.imageProcessing.toneMappingType = B.ImageProcessingConfiguration.TONEMAPPING_ACES
      pipe.imageProcessing.exposure = 1.55
      pipe.imageProcessing.contrast = 1.08
      pipe.imageProcessing.vignetteEnabled = true
      pipe.imageProcessing.vignetteWeight = 1.7
      pipe.imageProcessing.vignetteColor = new B.Color4(0.02, 0.01, 0.05, 0)
      pipe.grainEnabled = true
      pipe.grain.intensity = 7
      pipe.grain.animated = true

      /* walker state */
      let goalArt = 0
      let state: 'walk' | 'dwell' = 'walk'
      let dwellT = 0
      let playing = true
      let focused = -1
      const walker = new B.Vector3(0, 0, -10.5)
      let speed = 0
      let bobPhase = 0
      const moveDir = new B.Vector3(0, 0, 1)
      const lookPos = new B.Vector3(0, 2.0, 0)
      const camPos = new B.Vector3(0, EYE, -10.5)
      let yawOff = 0
      let pitchOff = 0
      const DWELL_MS = 3800
      const WALK_MAX = 1.22

      let dragging = false
      let lx = 0
      let ly = 0
      const onPointerDown = (e: PointerEvent) => {
        dragging = true
        lx = e.clientX
        ly = e.clientY
      }
      const onPointerMove = (e: PointerEvent) => {
        if (!dragging) return
        yawOff += (e.clientX - lx) * 0.0035
        pitchOff += (e.clientY - ly) * 0.0022
        pitchOff = Math.max(-0.5, Math.min(0.5, pitchOff))
        lx = e.clientX
        ly = e.clientY
      }
      const onPointerUp = () => {
        dragging = false
      }
      canvas.addEventListener('pointerdown', onPointerDown)
      window.addEventListener('pointermove', onPointerMove)
      window.addEventListener('pointerup', onPointerUp)

      toggleRef.current = () => {
        playing = !playing
        playingRef.current = playing
        onPlayingChange?.(playing)
      }

      /* ---------- DOM overlays projected from 3D ---------- */
      type Overlay = {
        el: HTMLDivElement
        getWorldPos: () => BABYLON.Vector3
        always?: boolean
        showWhenArt?: number
        orb?: BABYLON.Mesh
      }
      const overlays: Overlay[] = []
      const created: HTMLDivElement[] = []
      const overlayHost = host
      function addOverlay(el: HTMLDivElement, getWorldPos: () => BABYLON.Vector3, opts: Partial<Overlay> = {}) {
        overlayHost.appendChild(el)
        created.push(el)
        overlays.push({ el, getWorldPos, ...opts })
        return el
      }
      const px = (v: number) => `${v.toFixed(1)}px`
      function project(v3: BABYLON.Vector3) {
        const p = B.Vector3.Project(
          v3,
          B.Matrix.Identity(),
          scene.getTransformMatrix(),
          cam.viewport.toGlobal(engine.getRenderWidth(), engine.getRenderHeight()),
        )
        const scale = overlayHost.clientWidth / engine.getRenderWidth()
        return { x: p.x * scale, y: p.y * (overlayHost.clientHeight / engine.getRenderHeight()), z: p.z }
      }

      const pinArt = arts.find((a) => a.pin)
      if (pinArt) {
        const pinEl = document.createElement('div')
        pinEl.className = 'ds-room-pin'
        pinEl.textContent = 'S'
        Object.assign(pinEl.style, {
          position: 'absolute',
          width: '26px',
          height: '26px',
          margin: '-13px 0 0 -13px',
          borderRadius: '50% 50% 50% 4px',
          background: '#ffcf7d',
          color: '#4a3510',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '10.5px',
          fontWeight: '800',
          boxShadow: '0 4px 14px rgba(0,0,0,0.5), 0 0 22px rgba(255,207,125,0.4)',
          transition: 'opacity 250ms cubic-bezier(0.16, 1, 0.3, 1)',
        })
        addOverlay(pinEl, () => pinArt.center.add(new B.Vector3(-0.9, 0.55, -0.02)), {
          showWhenArt: arts.indexOf(pinArt),
        })

        const noteEl = document.createElement('div')
        Object.assign(noteEl.style, {
          position: 'absolute',
          width: '205px',
          margin: '-8px 0 0 18px',
          background: 'rgba(23,22,29,0.94)',
          border: '1px solid rgba(244,243,246,0.1)',
          borderRadius: '12px',
          padding: '10px 12px',
          backdropFilter: 'blur(14px)',
          boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
          transition: 'opacity 300ms cubic-bezier(0.16, 1, 0.3, 1)',
          fontFamily: 'var(--ds-font)',
        })
        noteEl.innerHTML =
          '<div style="display:flex;align-items:center;gap:7px;margin-bottom:4px;"><span style="width:17px;height:17px;border-radius:50%;font-size:8px;font-weight:800;display:flex;align-items:center;justify-content:center;background:#ffcf7d;color:#4a3510;">S</span><span style="font-size:11px;font-weight:650;color:#f4f3f6;">Sarah</span></div><div style="font-size:11.5px;line-height:1.45;color:rgba(244,243,246,0.6);">The look on your dad&rsquo;s face here. I&rsquo;m not crying, you&rsquo;re crying.</div>'
        addOverlay(noteEl, () => pinArt.center.add(new B.Vector3(-0.9, 0.55, -0.02)), {
          showWhenArt: arts.indexOf(pinArt),
        })
      }

      const voiceArt = arts.find((a) => a.voice)
      if (voiceArt) {
        const idx = arts.indexOf(voiceArt)
        const vEl = document.createElement('div')
        vEl.textContent = '▶'
        Object.assign(vEl.style, {
          position: 'absolute',
          width: '26px',
          height: '26px',
          margin: '-13px 0 0 -13px',
          borderRadius: '50% 50% 50% 4px',
          background: '#be9dfd',
          color: '#2d1a54',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '10.5px',
          fontWeight: '800',
          boxShadow: '0 4px 14px rgba(0,0,0,0.5), 0 0 22px rgba(190,157,253,0.45)',
          transition: 'opacity 250ms cubic-bezier(0.16, 1, 0.3, 1)',
        })
        addOverlay(vEl, () => voiceArt.center.add(new B.Vector3(0.02, -0.35, 0.7)), { showWhenArt: idx })

        const vTag = document.createElement('div')
        vTag.textContent = 'THEO · VOICE NOTE · 0:12'
        Object.assign(vTag.style, {
          position: 'absolute',
          transform: 'translate(-50%, -130%)',
          padding: '4px 10px',
          borderRadius: '999px',
          background: 'rgba(1,218,254,0.14)',
          border: '1px solid rgba(1,218,254,0.4)',
          color: '#01dafe',
          fontFamily: 'var(--ds-font-mono)',
          fontSize: '7.5px',
          letterSpacing: '0.12em',
          whiteSpace: 'nowrap',
          transition: 'opacity 250ms cubic-bezier(0.16, 1, 0.3, 1)',
        })
        addOverlay(vTag, () => voiceArt.center.add(new B.Vector3(0.02, 0.25, 0.7)), { showWhenArt: idx })
      }

      const mkOrb = (hex: string) => {
        const o = B.MeshBuilder.CreateSphere('o', { diameter: 0.09 }, scene)
        const m = new B.StandardMaterial('om', scene)
        m.emissiveColor = B.Color3.FromHexString(hex)
        m.disableLighting = true
        m.alpha = 0.9
        o.material = m
        return o
      }
      const mkTag = (text: string) => {
        const t = document.createElement('div')
        t.textContent = text
        Object.assign(t.style, {
          position: 'absolute',
          transform: 'translate(-50%, -130%)',
          padding: '4px 10px',
          borderRadius: '999px',
          background: 'rgba(1,218,254,0.14)',
          border: '1px solid rgba(1,218,254,0.4)',
          color: '#01dafe',
          fontFamily: 'var(--ds-font-mono)',
          fontSize: '7.5px',
          letterSpacing: '0.12em',
          whiteSpace: 'nowrap',
          transition: 'opacity 250ms cubic-bezier(0.16, 1, 0.3, 1)',
        })
        return t
      }
      const orb1 = mkOrb('#01dafe')
      const orb2 = mkOrb('#fea67d')
      const t1 = mkTag('PRIYA')
      addOverlay(t1, () => orb1.position.add(new B.Vector3(0, 0.22, 0)), { always: true, orb: orb1 })
      const t2 = mkTag('NOAH')
      addOverlay(t2, () => orb2.position.add(new B.Vector3(0, 0.22, 0)), { always: true, orb: orb2 })

      const heartArt = arts.find((a) => a.hearts)
      const heartIdx = heartArt ? arts.indexOf(heartArt) : -1
      const heartInterval = window.setInterval(() => {
        if (!heartArt) return
        if (focused !== heartIdx && Math.random() < 0.6) return
        const p = project(heartArt.center.add(new B.Vector3((Math.random() - 0.5) * 1.6, -0.4, -0.05)))
        if (p.z > 1 || p.z < 0) return
        const h = document.createElement('div')
        h.textContent = ['❤️', '🥂', '🥹'][Math.floor(Math.random() * 3)]
        Object.assign(h.style, {
          position: 'absolute',
          fontSize: '15px',
          left: px(p.x),
          top: px(p.y),
          animation: 'ds-room-heart 1.9s cubic-bezier(0.16,1,0.3,1) forwards',
        })
        host.appendChild(h)
        window.setTimeout(() => h.remove(), 1900)
      }, 1400)

      function setFocus(i: number) {
        if (i === focused) return
        focused = i
        if (i < 0) {
          onFocusChange?.(null)
          return
        }
        const a = arts[i]
        onFocusChange?.({ index: i, name: a.name, loves: a.loves, cms: a.cms, voice: a.voice, pin: a.pin })
      }

      /* ---------- Frame loop ---------- */
      const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v))
      const damp = (a: number, b: number, l: number, dt: number) => a + (b - a) * (1 - Math.exp(-l * dt))
      const dampV3 = (a: BABYLON.Vector3, b: BABYLON.Vector3, l: number, dt: number) =>
        B.Vector3.LerpToRef(a, b, 1 - Math.exp(-l * dt), a)

      let t0 = performance.now()
      engine.runRenderLoop(() => {
        const now = performance.now()
        const dt = Math.min(50, now - t0)
        t0 = now
        const dts = dt / 1000
        const tt = now / 1000

        const art = arts[goalArt]
        let gaze: BABYLON.Vector3

        if (playing) {
          if (state === 'walk') {
            const goal = new B.Vector3(art.viewPos.x, 0, art.viewPos.z)
            const toGoal = goal.subtract(walker)
            toGoal.y = 0
            const dist = toGoal.length()
            const targetSpeed = clamp(dist / 1.5, 0, 1) * WALK_MAX
            speed = damp(speed, targetSpeed, 2.1, dts)
            if (dist > 0.004) {
              const d = toGoal.normalize()
              dampV3(moveDir, d, 3.2, dts)
              moveDir.normalize()
            }
            walker.addInPlace(moveDir.scale(speed * dts))
            if (dist < 0.1) {
              state = 'dwell'
              dwellT = 0
            }
            const ahead = walker.add(moveDir.scale(4.5))
            ahead.y = EYE + 0.06
            const blend = clamp(1 - dist / 5.5, 0, 1)
            gaze = B.Vector3.Lerp(ahead, art.center, blend * blend)
            setFocus(dist < 2.4 ? goalArt : -1)
          } else {
            dwellT += dt
            speed = damp(speed, 0, 4.5, dts)
            setFocus(goalArt)
            const wallDir = new B.Vector3(art.normal.z, 0, -art.normal.x)
            const scanX = Math.sin(dwellT / 1500) * art.w * 0.22
            const scanY = Math.sin(dwellT / 2300 + 1.2) * 0.14
            gaze = art.center.add(wallDir.scale(scanX)).add(new B.Vector3(0, scanY, 0))
            if (dwellT > DWELL_MS) {
              goalArt += 1
              if (goalArt >= arts.length) {
                fade.style.opacity = '1'
                window.setTimeout(() => {
                  walker.set(0, 0, -10.5)
                  moveDir.set(0, 0, 1)
                  camPos.set(0, EYE, -10.5)
                  lookPos.set(0, 2, 4)
                  fade.style.opacity = '0'
                }, 520)
                goalArt = 0
              }
              state = 'walk'
            }
          }
          dampV3(lookPos, gaze, 2.6, dts)
          if (!dragging) {
            yawOff *= Math.exp(-2.2 * dts)
            pitchOff *= Math.exp(-2.2 * dts)
          }
        } else {
          speed = damp(speed, 0, 6, dts)
        }

        const sNorm = speed / WALK_MAX
        bobPhase += speed * dts * 3.4
        const bobY = Math.sin(bobPhase * 2) * 0.028 * sNorm + Math.sin(tt * 1.15) * 0.007
        const sway = Math.sin(bobPhase) * 0.018 * sNorm
        const right = new B.Vector3(moveDir.z, 0, -moveDir.x)
        const eyeTarget = walker.add(new B.Vector3(0, EYE + bobY, 0)).add(right.scale(sway))
        dampV3(camPos, eyeTarget, 12, dts)
        cam.position.copyFrom(camPos)

        const dir = lookPos.subtract(camPos)
        const yaw = Math.atan2(dir.x, dir.z) + yawOff
        const pitch = -Math.atan2(dir.y, Math.sqrt(dir.x * dir.x + dir.z * dir.z)) + pitchOff
        const roll = -sway * 0.55 + Math.sin(tt * 0.9) * 0.0016
        cam.rotation.set(pitch, yaw, roll)

        orb1.position.set(Math.sin(tt * 0.31) * 2.6, 1.45 + Math.sin(tt * 0.9) * 0.06, 2 + Math.sin(tt * 0.17) * 7)
        orb2.position.set(
          Math.sin(tt * 0.23 + 2) * 3.1,
          1.5 + Math.cos(tt * 0.8) * 0.06,
          6 + Math.cos(tt * 0.13) * 6,
        );
        [orb1, orb2].forEach((o) => {
          o.visibility = clamp((B.Vector3.Distance(o.position, camPos) - 1.6) / 1.2, 0, 1)
        })

        overlays.forEach((o) => {
          const p = project(o.getWorldPos())
          const visible = p.z > 0 && p.z < 1 && (o.always || o.showWhenArt === focused) && (!o.orb || o.orb.visibility > 0.5)
          o.el.style.opacity = visible ? '1' : '0'
          o.el.style.left = px(p.x)
          o.el.style.top = px(p.y)
        })

        scene.render()
      })

      const onResize = () => engine.resize()
      window.addEventListener('resize', onResize)

      return () => {
        window.clearInterval(heartInterval)
        window.removeEventListener('resize', onResize)
        canvas.removeEventListener('pointerdown', onPointerDown)
        window.removeEventListener('pointermove', onPointerMove)
        window.removeEventListener('pointerup', onPointerUp)
        created.forEach((el) => el.remove())
        engine.stopRenderLoop()
        scene.dispose()
        engine.dispose()
      }
      // artworks/imageBase are treated as fixed for the lifetime of one mount —
      // this is a heavy imperative scene, not something to rebuild on prop churn.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
      <Root className={className}>
        <Canvas ref={canvasRef} />
        <OverlayHost ref={hostRef} />
        <LoopFade ref={fadeRef} />
        {children}
        <style>{`@keyframes ds-room-heart { 0% { opacity: 0; transform: translate(-50%, 0) scale(0.6); } 14% { opacity: 1; } 100% { opacity: 0; transform: translate(-50%, -88px) scale(1.3); } }`}</style>
      </Root>
    )
  },
)
